/**
 * Lightweight date formatting utilities to replace date-fns
 * This reduces bundle size from ~150KB to ~2KB
 */

// Polish month names
const POLISH_MONTHS = [
    'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
    'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'
];

// Polish day names
const POLISH_DAYS = [
    'Niedziela', 'Poniedziałek', 'Wtorek', 'Środa',
    'Czwartek', 'Piątek', 'Sobota'
];

/**
 * Parse ISO date string to Date object
 */
export function parseISO(dateString: string): Date {
    return new Date(dateString);
}

/**
 * Check if date is valid
 */
export function isValid(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Convert Unix timestamp to Date
 */
export function fromUnixTime(timestamp: number): Date {
    return new Date(timestamp * 1000);
}

/**
 * Pad number with leading zero if needed
 */
function pad(num: number): string {
    return num.toString().padStart(2, '0');
}

/**
 * Format date to DD.MM.YYYY HH:mm
 */
export function formatDate(rawDate: string): string {
    if (!rawDate) return '';

    const date = parseISO(rawDate);
    if (!isValid(date)) return '';

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Format millisecond timestamp to DD.MM.YYYY
 */
export function formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    if (!isValid(date)) return '';

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
}

/**
 * Format ISO date string to DD.MM.YYYY
 */
export function formatISODateString(dateString: string): string {
    const date = parseISO(dateString);
    if (!isValid(date)) return '';

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
}

/**
 * Format date with "o godzinie" text
 */
export function formatDateWithHourWord(rawDate: string): string {
    const formattedDate = formatDate(rawDate);
    if (!formattedDate) return '';

    const [day, time] = formattedDate.split(' ');
    return `${day} o godzinie ${time}`;
}

/**
 * Format date with "o" text
 */
export function formatDateWithOWord(rawDate: string): string {
    const formattedDate = formatDate(rawDate);
    if (!formattedDate) return '';

    const [day, time] = formattedDate.split(' ');
    return `${day} o ${time}`;
}

/**
 * Format day of week and time in Polish
 */
export function formatDayOfWeekAndTime(rawDate: string): string {
    if (!rawDate) return '';

    const date = parseISO(rawDate);
    if (!isValid(date)) return '';

    const dayOfWeek = POLISH_DAYS[date.getDay()];
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${dayOfWeek} ${hours}:${minutes}`;
}

/**
 * Custom format function for more complex formatting
 * Supports basic patterns: dd, MM, yyyy, HH, mm
 */
export function format(date: Date, pattern: string, options?: { locale?: string }): string {
    if (!isValid(date)) return '';

    let formatted = pattern;

    // Basic replacements
    formatted = formatted.replace('yyyy', date.getFullYear().toString());
    formatted = formatted.replace('MM', pad(date.getMonth() + 1));
    formatted = formatted.replace('dd', pad(date.getDate()));
    formatted = formatted.replace('HH', pad(date.getHours()));
    formatted = formatted.replace('mm', pad(date.getMinutes()));
    formatted = formatted.replace('ss', pad(date.getSeconds()));

    // Polish locale support
    if (options?.locale === 'pl') {
        // Replace month names
        formatted = formatted.replace('MMMM', POLISH_MONTHS[date.getMonth()]);
        // Replace day names
        formatted = formatted.replace('EEEE', POLISH_DAYS[date.getDay()].toLowerCase());
    }

    return formatted;
}

/**
 * Format meeting date in Polish format: "Sobota, Październik 14, 2025 18:00"
 */
export function formatMeetingDate(dateString: string): string {
    if (!dateString) return '';

    const date = parseISO(dateString);
    if (!isValid(date)) return '';

    const dayOfWeek = POLISH_DAYS[date.getDay()];
    const monthName = POLISH_MONTHS[date.getMonth()].charAt(0).toUpperCase() + POLISH_MONTHS[date.getMonth()].slice(1);
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${dayOfWeek}, ${day} ${monthName}, ${hours}:${minutes}`;
}

// ─── Poland timezone helpers ────────────────────────────────────────────────

/**
 * Get a Date-like object whose getFullYear/getMonth/getDate/getDay/getHours
 * return values in Europe/Warsaw timezone.
 * Uses Intl.DateTimeFormat so no extra dependencies are needed.
 */
export function getNowInPoland(): Date {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Warsaw',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(now);

    const get = (type: string) => parts.find(p => p.type === type)!.value;
    return new Date(
        parseInt(get('year')),
        parseInt(get('month')) - 1,
        parseInt(get('day')),
        parseInt(get('hour')),
        parseInt(get('minute')),
        parseInt(get('second'))
    );
}

/**
 * Today's date in Poland as "YYYY-MM-DD".
 * Mirrors the backend's getTodaysDateInPoland() from core_layer/util.ts.
 */
export function getTodaysDateInPoland(): string {
    const d = getNowInPoland();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * Polish pluralization: returns correct form for count.
 * singular (1): "krok"
 * paucal (2-4, 22-24, 32-34...): "kroki"
 * plural (0, 5-21, 25-31...): "kroków"
 */
export function polishPlural(count: number, singular: string, paucal: string, plural: string): string {
    const absCount = Math.abs(count);
    if (absCount === 1) return singular;
    const lastTwo = absCount % 100;
    const lastOne = absCount % 10;
    if (lastTwo >= 12 && lastTwo <= 14) return plural;
    if (lastOne >= 2 && lastOne <= 4) return paucal;
    return plural;
}

// Export a mock locale object for compatibility
export const pl = {code: 'pl'};